import { useCallback, useState } from "react";
import styled from "styled-components";
import SearchIcon from "@mui/icons-material/Search";

import {
  BuiltInEdge,
  useReactFlow,
  type Node,
  type PanelProps,
} from "@xyflow/react";

import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "cmdk";

import { getCurrentColors } from "../utils/colors";

const COLORS = getCurrentColors();

const SearchContainer = styled(Command)`
  position: relative;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 4px;
  min-width: 300px;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus-within {
    border-color: ${COLORS.botaoFormulario};
    box-shadow: 0 0 0 1px ${COLORS.botaoFormulario};
  }
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 8px;
  gap: 8px;

  svg {
    color: rgba(0, 0, 0, 0.54);
    font-size: 20px;
    flex-shrink: 0;
  }
`;

const StyledCommandInput = styled(CommandInput)`
  border: none;
  outline: none;
  width: 100%;
  font-size: 13px;
  padding: 4.5px 0;
  background: transparent;
  color: ${COLORS.cinzaEscuro};

  &::placeholder {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.42);
  }
`;

const StyledCommandList = styled(CommandList)`
  border-top: 1px solid ${COLORS.cinzaDesbotado};
  max-height: 300px;
  overflow-y: auto;
`;

const StyledCommandEmpty = styled(CommandEmpty)`
  padding: 12px 16px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
  text-align: center;
`;

const StyledCommandGroup = styled(CommandGroup)`
  [cmdk-group-heading] {
    padding: 6px 16px;
    font-size: 11px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.54);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const StyledCommandItem = styled(CommandItem)`
  padding: 8px 16px;
  font-size: 13px;
  cursor: pointer;
  color: ${COLORS.cinzaEscuro};

  &[aria-selected="true"],
  &[data-selected="true"] {
    background-color: ${COLORS.primaria};
    color: ${COLORS.primariaEscura};
  }

  &:hover {
    background-color: ${COLORS.cinzaClaro};
  }
`;

export interface NodeSearchProps extends Omit<PanelProps, "children"> {
  onSearch?: (searchString: string) => Node[];
  onSelectNode?: (node: Node) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function NodeSearchInternal({
  className,
  onSearch,
  onSelectNode,
  open,
  onOpenChange,
  ...props
}: NodeSearchProps) {
  const [searchResults, setSearchResults] = useState<Node[]>([]);
  const [searchString, setSearchString] = useState<string>("");
  const { getNodes, fitView, setNodes } = useReactFlow<Node, BuiltInEdge>();

  const defaultOnSearch = useCallback(
    (searchString: string) => {
      const nodes = getNodes();
      return nodes.filter((node) =>
        (node.data.rawLabel as string)
          .toLowerCase()
          .includes(searchString.toLowerCase()),
      );
    },
    [getNodes],
  );

  const onChange = useCallback(
    (searchString: string) => {
      setSearchString(searchString);
      if (searchString.length > 0) {
        onOpenChange?.(true);
        const results = (onSearch || defaultOnSearch)(searchString);
        setSearchResults(results);
      }
    },
    [onSearch, onOpenChange],
  );

  const defaultOnSelectNode = useCallback(
    (node: Node) => {
      setNodes((nodes) =>
        nodes.map((n) => (n.id === node.id ? { ...n, selected: true } : n)),
      );
      fitView({ nodes: [node], duration: 500 });
    },
    [fitView, setNodes],
  );

  const onSelect = useCallback(
    (node: Node) => {
      (onSelectNode || defaultOnSelectNode)?.(node);
      setSearchString("");
      onOpenChange?.(false);
    },
    [onSelectNode, defaultOnSelectNode, onOpenChange],
  );

  return (
    <>
      <InputWrapper>
        <SearchIcon />
        <StyledCommandInput
          placeholder="Procurar pergunta"
          onValueChange={onChange}
          value={searchString}
          onFocus={() => onOpenChange?.(true)}
        />
      </InputWrapper>

      {open && (
        <StyledCommandList>
          {searchResults.length === 0 ? (
            <StyledCommandEmpty>
              Nenhum resultado para "{searchString}"
            </StyledCommandEmpty>
          ) : (
            <StyledCommandGroup heading="Nodes">
              {searchResults.map((node) => (
                <StyledCommandItem key={node.id} onSelect={() => onSelect(node)}>
                  {node.data.rawLabel as string}
                </StyledCommandItem>
              ))}
            </StyledCommandGroup>
          )}
        </StyledCommandList>
      )}
    </>
  );
}

export function NodeSearch({
  className,
  onSearch,
  onSelectNode,
  ...props
}: NodeSearchProps) {
  const [open, setOpen] = useState(false);
  return (
    <SearchContainer shouldFilter={false}>
      <NodeSearchInternal
        className={className}
        onSearch={onSearch}
        onSelectNode={onSelectNode}
        open={open}
        onOpenChange={setOpen}
        {...props}
      />
    </SearchContainer>
  );
}

export interface NodeSearchDialogProps extends NodeSearchProps {
  title?: string;
}

export function NodeSearchDialog({
  className,
  onSearch,
  onSelectNode,
  open,
  onOpenChange,
  title = "Node Search",
  ...props
}: NodeSearchDialogProps) {
  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <NodeSearchInternal
        className={className}
        onSearch={onSearch}
        onSelectNode={onSelectNode}
        open={open}
        onOpenChange={onOpenChange}
        {...props}
      />
    </CommandDialog>
  );
}
